import { useEffect, useState } from "react";
import MagneticButton from "../components/anim/MagneticButton";
import {
  Briefcase,
  Send,
  X,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const Careers = () => {
  const [openForm, setOpenForm] = useState(false);

  const navigate = useNavigate();

  // FORM STATES
  const [sending, setSending] = useState(false);
  const [utmSource, setUtmSource] = useState("google");
  const [formError, setFormError] = useState({});
  const generateCaptcha = () => {
    return Math.random()
      .toString(36)
      .substring(2, 8);
  };

  const [captchaText, setCaptchaText] = useState(generateCaptcha());

  const [captchaInput, setCaptchaInput] = useState("");


  const currentDate = new Date()
    .toLocaleDateString("en-GB")
    .replace(/-/g, "/");

  const initialValues = {
    name: "",
    email: "",
    position: "",
    resume: null,
    date: currentDate,
    leadSource: utmSource,
  };

  const [data, setData] = useState(initialValues);

  // UTM
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const utm_source =
      urlParams.get("utm_source") || "google";

    setUtmSource(utm_source);

    setData((prevData) => ({
      ...prevData,
      leadSource: utm_source,
    }));
  }, []);
  useEffect(() => {
    if (openForm) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }

    return () => {
      document.body.style.overflow = "auto";
    };
  }, [openForm]);



  // IP
  useEffect(() => {
    fetch("https://api.ipify.org?format=json")
      .then((res) => res.json())
      .then((ipData) => {
        setData((prev) => ({
          ...prev,
          ipAddress: ipData.ip,
        }));
      })
      .catch(() => {
        console.log("Failed to fetch IP");
      });
  }, []);

  // BASE64 CONVERTER
  const convertToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.readAsDataURL(file);

      reader.onload = () => {
        resolve(reader.result);
      };

      reader.onerror = (error) => {
        reject(error);
      };
    });
  };

  // VALIDATION
  const validate = (values) => {
    const error = {};

    if (!values.name)
      error.name = "Name is required";

    if (!values.email) {
      error.email = "Email is required";
    } else {
      const emailRegex =
        /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/;

      if (!emailRegex.test(values.email)) {
        error.email = "Invalid email";
      }
    }
    if (captchaInput !== captchaText) {
      error.captcha = "Invalid captcha";
    }

    if (!values.position)
      error.position = "Position is required";

    if (!values.resume) {
      error.resume = "Resume is required";
    } else if (values.resume instanceof File) {
      if (values.resume.size > 5 * 1024 * 1024) {
        error.resume = "File size should not exceed 5MB";
      } else if (!values.resume.name.toLowerCase().endsWith(".pdf") && values.resume.type !== "application/pdf") {
        error.resume = "Only PDF files are allowed";
      }
    }

    return error;
  };

  // SEND MAIL
  const sendMail = async (e) => {
    e.preventDefault();

    const validationErrors = validate(data);

    setFormError(validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      return;
    }

    try {
      setSending(true);

      let resumeBase64 = "";

      if (data.resume) {
        resumeBase64 = await convertToBase64(data.resume);

        // remove base64 prefix
        resumeBase64 = resumeBase64.split(",")[1];
        console.log(resumeBase64);
      }

      const emailBody = `
<div style="font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; max-width: 600px; margin: 0 auto; border: 1px solid #e2e8f0; border-radius: 16px; overflow: hidden; background-color: #ffffff; box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);">
  <!-- Premium Header -->
  <div style="background-color: #0b1e36; padding: 28px 32px; border-bottom: 4px solid #f59e0b;">
    <h2 style="color: #ffffff; margin: 0; font-size: 22px; font-weight: 700; letter-spacing: 0.5px; text-transform: uppercase;">Nikhan Controls</h2>
    <p style="color: #f59e0b; margin: 6px 0 0 0; font-size: 11px; font-weight: 600; letter-spacing: 1.5px; text-transform: uppercase;">Careers Portal / Application</p>
  </div>
  
  <!-- Content -->
  <div style="padding: 32px; background-color: #ffffff;">
    <p style="color: #475569; font-size: 15px; line-height: 1.6; margin-top: 0; margin-bottom: 28px;">A new candidate has successfully submitted an application through the careers portal. Details are listed below:</p>
    
    <table style="width: 100%; border-collapse: collapse;">
      <tr>
        <td style="padding: 14px 0; border-bottom: 1px solid #f1f5f9; color: #64748b; font-size: 14px; font-weight: 600; width: 32%;">Candidate Name</td>
        <td style="padding: 14px 0; border-bottom: 1px solid #f1f5f9; color: #0f172a; font-size: 14px; font-weight: 500;">${data.name}</td>
      </tr>
      <tr>
        <td style="padding: 14px 0; border-bottom: 1px solid #f1f5f9; color: #64748b; font-size: 14px; font-weight: 600;">Email Address</td>
        <td style="padding: 14px 0; border-bottom: 1px solid #f1f5f9; color: #3b82f6; font-size: 14px; font-weight: 500;"><a href="mailto:${data.email}" style="color: #3b82f6; text-decoration: none; font-weight: 600;">${data.email}</a></td>
      </tr>
      <tr>
        <td style="padding: 14px 0; border-bottom: 1px solid #f1f5f9; color: #64748b; font-size: 14px; font-weight: 600;">Target Position</td>
        <td style="padding: 14px 0; border-bottom: 1px solid #f1f5f9; color: #0f172a; font-size: 14px; font-weight: 600;"><span style="background-color: #fef3c7; color: #d97706; padding: 4px 10px; border-radius: 6px; font-size: 13px; font-weight: 600;">${data.position}</span></td>
      </tr>
      <tr>
        <td style="padding: 14px 0; border-bottom: 1px solid #f1f5f9; color: #64748b; font-size: 14px; font-weight: 600;">Date Submitted</td>
        <td style="padding: 14px 0; border-bottom: 1px solid #f1f5f9; color: #0f172a; font-size: 14px; font-weight: 500;">${data.date}</td>
      </tr>
    </table>
  </div>
  
  <!-- Footer -->
  <div style="background-color: #f8fafc; padding: 24px 32px; border-top: 1px solid #e2e8f0; font-size: 12px; color: #64748b;">
    <div style="display: flex; justify-content: space-between; flex-wrap: wrap;">
      <div style="margin-bottom: 8px; margin-right: 16px;"><strong>Lead Source:</strong> ${data.leadSource || "google"}</div>
      <div style="margin-bottom: 8px;"><strong>IP Address:</strong> ${data.ipAddress || "Not Provided"}</div>
    </div>
    <div style="margin-top: 16px; border-top: 1px dashed #e2e8f0; padding-top: 16px; text-align: center; color: #94a3b8;">
      This email is automatically generated by the Nikhan Controls Careers System.
    </div>
  </div>
</div>
`.replace(/\r?\n/g, " ");

      const sanitizedFileName = data.resume ? data.resume.name.replace(/[^a-zA-Z0-9._-]/g, "_") : "";

      const endpoint = process.env.REACT_APP_ENDPOINT_MAIL_URL;
      const mailURL = `${window.location.origin}${endpoint}?token=${process.env.REACT_APP_SEND_MAIL_ATTACHMENT_TOKEN}`;

      const res = await fetch(mailURL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          subject: "Resume Application",
          body: emailBody,
          emailAttachments: data.resume
            ? [
              {
                fileName: sanitizedFileName,
                content: resumeBase64,
              },
            ]
            : []
        })
        ,
      });

      await res.text();

      if (res.ok) {
        // alert("Application submitted successfully");

        setData(initialValues);

        setOpenForm(false);
        navigate('/thank-you');
      } else {
        alert("Failed to send application");
      }
    } catch (error) {
      console.error(error);
      alert("Something went wrong");
    } finally {
      setSending(false);
    }
  };

  // INPUT CHANGE
  const change = (e) => {
    const { name, value, files } = e.target;

    // FILE VALIDATION
    if (files && files[0]) {
      const file = files[0];

      // Check if it is a PDF
      if (!file.name.toLowerCase().endsWith(".pdf") && file.type !== "application/pdf") {
        setFormError((prev) => ({
          ...prev,
          resume: "Only PDF files are allowed",
        }));

        e.target.value = "";

        setData((prev) => ({
          ...prev,
          resume: null,
        }));

        return;
      }

      // 5MB limit
      const maxSize = 5 * 1024 * 1024;

      if (file.size > maxSize) {
        setFormError((prev) => ({
          ...prev,
          resume: "File size should not exceed 5MB",
        }));

        e.target.value = "";

        setData((prev) => ({
          ...prev,
          resume: null,
        }));

        return;
      } else {
        // Clear file error if file is within limit
        setFormError((prev) => ({
          ...prev,
          resume: undefined,
        }));
      }
    }

    setData((prev) => ({
      ...prev,
      [name]: files ? files[0] : value,
      leadSource: utmSource,
    }));
  };

  return (
    <>
      <section
        id="careers"
        data-testid="v4-careers"
        className="relative py-24 md:py-32 bg-navy-900 text-white overflow-hidden scroll-mt-28"
      >
        <div className="absolute inset-0 dot-pattern-dark opacity-40 pointer-events-none" />

        <div className="absolute -top-40 -right-20 w-[500px] h-[500px] bg-amber-500/15 rounded-full blur-[140px] pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-5 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="text-[11px] uppercase tracking-[0.22em] text-amber-500 font-bold flex items-center gap-2">
              <span className="w-8 h-px bg-amber-500" />
              Careers
            </div>

            <h2 className="mt-4 font-display text-3xl md:text-5xl lg:text-6xl font-bold tracking-[-0.03em] leading-[1]">
              Build the future
              <br />
              <span className="text-amber-500">
                with us.
              </span>
            </h2>

            <p className="mt-6 text-slate-300 text-base md:text-lg leading-relaxed max-w-xl">
              We're growing our engineering,
              manufacturing and quality teams in
              Mohali. If you love building
              electronics that ship at scale,
              we'd love to hear from you.
            </p>
          </div>

          <div className="lg:col-span-5">
            <div className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur p-7">
              <Briefcase className="w-7 h-7 text-amber-500 mb-4" />

              <div className="text-[11px] uppercase tracking-[0.22em] text-amber-500 font-bold">
                Open Roles
              </div>

              <ul className="mt-5 space-y-3 text-sm">
                {[
                  "Embedded Firmware Engineer",
                  "Power Electronics Engineer",
                  "SMT Line Operator",
                  "QA Test Engineer",
                  "Mechanical Designer",
                ].map((r) => (
                  <li
                    key={r}
                    className="flex items-center justify-between gap-3 py-2 border-b border-white/10"
                  >
                    <span className="text-white">
                      {r}
                    </span>

                    <button
                      onClick={() => {
                        setData((prev) => ({
                          ...prev,
                          position: r,
                        }));

                        setOpenForm(true);
                      }}
                      className="text-[11px] uppercase tracking-[0.18em] text-amber-500 font-bold"
                    >
                      Apply
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* MODAL */}
      {openForm && (
        <div className="fixed inset-0 !z-[2147483647] bg-black/70 backdrop-blur-sm overflow-hidden max-sm:mt-6">
          <div className="min-h-[100dvh] flex items-start justify-center px-4 py-20 max-sm:py-25 max-sm:min-h-[80dvh]">
            <div className="relative w-full max-w-3xl">
              {/* CLOSE */}
              <button
                onClick={() =>
                  setOpenForm(false)
                }
                className="absolute top-[60px] max-sm:top-[13px] md:top-[15rem] lg:top-[2.5rem] xl:top-[4.5rem] right-4 z-50 w-10 h-10 rounded-full bg-white text-navy-900 flex items-center justify-center shadow-lg"              >
                <X className="w-5 h-5" />
              </button>

              {/* FORM */}
              <form
                onSubmit={sendMail}
                data-testid="v4-contact-form"
                className="max-h-[80vh] overflow-y-auto rounded-3xl border border-slate-200 bg-white p-7 max-sm:p-[1.25rem] md:p-[1.25rem] md:mt-[14rem] lg:mt-[2rem] xl:mt-[4rem] shadow-[0_8px_40px_-12px_rgba(10,37,64,0.1)]"              >
                <h3 className="font-display text-2xl md:text-3xl font-bold text-navy-900 tracking-tight">
                  Apply for Position
                </h3>

                <p className="mt-2 text-sm text-slate-600">
                  Share your details — our hiring
                  team will contact you.
                </p>

                <div className="mt-8 max-sm:mt-[.9rem] grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* NAME */}
                  <div>
                    <label className="text-[11px] uppercase tracking-[0.22em] font-bold text-navy-800">
                      Full Name *
                    </label>

                    <input
                      required
                      type="text"
                      name="name"
                      value={data.name}
                      onChange={change}
                      className="mt-2 w-full bg-transparent border-b-2 border-slate-200 focus:border-amber-500 outline-none py-3 max-sm:pt-0 text-navy-900 placeholder:text-slate-400 transition-colors"
                      placeholder="Enter your Name"
                    />

                    {formError.name && (
                      <p className="text-red-500 text-xs mt-1">
                        {formError.name}
                      </p>
                    )}
                  </div>

                  {/* EMAIL */}
                  <div>
                    <label className="text-[11px] uppercase tracking-[0.22em] font-bold text-navy-800">
                      Email *
                    </label>

                    <input
                      required
                      type="email"
                      name="email"
                      value={data.email}
                      onChange={change}
                      className="mt-2 w-full bg-transparent border-b-2 border-slate-200 focus:border-amber-500 outline-none py-3 max-sm:pt-0 text-navy-900 placeholder:text-slate-400 transition-colors"
                      placeholder="Enter your Email Address"
                    />

                    {formError.email && (
                      <p className="text-red-500 text-xs mt-1">
                        {formError.email}
                      </p>
                    )}
                  </div>

                  {/* POSITION */}
                  <div>
                    <label className="text-[11px] uppercase tracking-[0.22em] font-bold text-navy-800">
                      Position *
                    </label>

                    <input
                      required
                      type="text"
                      name="position"
                      value={data.position}
                      readOnly
                      className="mt-2 w-full bg-transparent border-b-2 border-slate-200 outline-none py-3  max-sm:pt-0 text-navy-900"
                    />

                    {formError.position && (
                      <p className="text-red-500 text-xs mt-1">
                        {formError.position}
                      </p>
                    )}
                  </div>

                  {/* RESUME */}
                  <div>
                    <label className="text-[11px] uppercase tracking-[0.22em] font-bold text-navy-800">
                      Resume (Max 5MB) *
                    </label>

                    <input
                      required
                      type="file"
                      name="resume"
                      accept=".pdf"
                      onChange={change}
                      className="mt-3 block w-full text-sm text-navy-900"
                    />

                    {formError.resume ? (
                      <p className="text-red-500 text-xs mt-1">
                        {formError.resume}
                      </p>
                    ) : (
                      <p className="text-slate-400 text-[10px] mt-1">
                        Accepted format: PDF (Max 5MB)
                      </p>
                    )}
                  </div>


                  {/* CAPTCHA */}
                  <div className="sm:col-span-2 mt-1">
                    <label className="text-[11px] uppercase tracking-[0.22em] font-bold text-navy-800">
                      Captcha *
                    </label>

                    <div className="mt-3 flex items-center gap-3 flex-nowrap">
                      <input
                        type="text"
                        placeholder="Enter Captcha"
                        value={captchaInput}
                        onChange={(e) =>
                          setCaptchaInput(e.target.value)
                        }
                        className="w-[180px] max-sm:w-[150px] bg-transparent border-b-2 border-slate-200 focus:border-amber-500 outline-none py-2 text-navy-900 placeholder:text-slate-400 transition-colors"
                      />

                      <button
                        type="button"
                        onClick={() => {
                          setCaptchaText(generateCaptcha());
                          setCaptchaInput("");
                        }}
                        className="text-2xl text-navy-900 shrink-0"
                      >
                        ↻
                      </button>

                      <div className="text-[1.25rem] tracking-[0.25em] text-navy-900 font-semibold italic select-none whitespace-nowrap">
                        {captchaText}
                      </div>
                    </div>

                    {formError.captcha && (
                      <p className="text-red-500 text-xs mt-2">
                        {formError.captcha}
                      </p>
                    )}
                  </div>
                </div>

                <MagneticButton
                  strength={0.35}
                  className="mt-10 inline-block"
                >
                  <button
                    type="submit"
                    disabled={sending}
                    className="group inline-flex items-center gap-2 px-7 py-4 rounded-full bg-navy-900 hover:bg-navy-800 text-white font-semibold tracking-tight transition-all hover:gap-3 disabled:opacity-60"
                  >
                    {sending
                      ? "Sending..."
                      : "Submit Application"}

                    <Send className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                  </button>
                </MagneticButton>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Careers;